import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';

export default function BilanPage() {
  // Identite du TENANT pour l'en-tete/pied des exports PDF.
  // Sans raison sociale, NIF et RCCM, l'export est REFUSE (cf. utils/exportPdf.js).
  const tenant = useTenant();
  const [bilan, setBilan] = useState({ actif: { tresorerie: 0, creances: 0, stocks: 0, immobilisations: 0 }, passif: { capital: 1000000, resultat: 0, dettes_fournisseurs: 0, dettes_sociales: 0 } });
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  const load = useCallback(async () => {
    try { const r = await api.get('/comptabilite/bilan'); setBilan(r.data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const a = bilan.actif;
  const p = bilan.passif;
  const totalActif = a.tresorerie + a.creances + a.stocks + a.immobilisations;
  const totalPassif = p.capital + p.resultat + p.dettes_fournisseurs + p.dettes_sociales;
  const isEmpty = totalActif === 0;

  const Row = ({ label, value, bold, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: bold ? 700 : 400, color: color || 'inherit', fontSize: bold ? 15 : 13 }}>
      <span>{label}</span>
      <span>{Number(value).toLocaleString()} FCFA</span>
    </div>
  );

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--gss-text-light)' }}>Chargement...</p>;

  return (
    <div>
      <div className="gss-toolbar" style={{ marginBottom: 16 }}>
        <div />
        <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf('Bilan comptable', printRef.current, tenant)}>Exporter PDF</button>
      </div>

      <div ref={printRef}>
        {isEmpty ? (
          <div className="content-card" style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>&#9898;</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#555', marginBottom: 8 }}>Aucune donnée saisie</div>
            <div style={{ fontSize: 13, color: '#888', lineHeight: 1.6, maxWidth: 400, margin: '0 auto' }}>
              Alimentez la trésorerie, les stocks et les créances pour voir le bilan réel.
              <br />Les données apparaîtront automatiquement une fois les mouvements enregistrés.
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="content-card">
                <div className="content-card-title" style={{ color: '#2e7d32' }}>ACTIF</div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ padding: '6px 16px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Immobilisations</div>
                  <Row label="Véhicules & Matériels" value={a.immobilisations} />
                  <div style={{ padding: '6px 16px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginTop: 8 }}>Actif circulant</div>
                  <Row label="Stocks (équipements)" value={a.stocks} />
                  <Row label="Créances clients" value={a.creances} />
                  <div style={{ padding: '6px 16px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginTop: 8 }}>Trésorerie</div>
                  <Row label="Caisse + Banque" value={a.tresorerie} />
                  <div style={{ marginTop: 12, borderTop: '2px solid #2e7d32' }}>
                    <Row label="TOTAL ACTIF" value={totalActif} bold color="#2e7d32" />
                  </div>
                </div>
              </div>

              <div className="content-card">
                <div className="content-card-title" style={{ color: '#1565c0' }}>PASSIF</div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ padding: '6px 16px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Capitaux propres</div>
                  <Row label="Capital social" value={p.capital} />
                  <Row label="Résultat exercice" value={p.resultat} />
                  <div style={{ padding: '6px 16px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginTop: 8 }}>Dettes</div>
                  <Row label="Dettes fournisseurs" value={p.dettes_fournisseurs} />
                  <Row label="Dettes sociales (CNSS)" value={p.dettes_sociales} />
                  <div style={{ marginTop: 12, borderTop: '2px solid #1565c0' }}>
                    <Row label="TOTAL PASSIF" value={totalPassif} bold color="#1565c0" />
                  </div>
                </div>
              </div>
            </div>

            <div className="content-card" style={{ marginTop: 16, textAlign: 'center', padding: '1rem' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: totalActif === totalPassif ? '#2e7d32' : '#DC2626' }}>
                {totalActif === totalPassif ? 'Bilan équilibré — Total Actif = Total Passif' : `Écart : ${(totalActif - totalPassif).toLocaleString()} FCFA`}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
